<template>
  <div class="user-status-component">
    <!-- Status-Indikator mit Menü -->
    <v-menu v-if="authStore.isAuthenticated && !showDropdownContent" location="bottom end" :close-on-content-click="false">
      <template v-slot:activator="{ props }">
        <div class="status-indicator" v-bind="props">
          <v-badge
            :color="statusColor"
            dot
            bordered
            location="bottom end"
            v-if="!hideAvatar"
          >
            <v-avatar size="40" color="grey-darken-3">
              <v-icon v-if="!user?.vorname">mdi-account</v-icon>
              <span v-else>{{ getInitials(user.vorname, user.nachname) }}</span>
            </v-avatar>
          </v-badge>
          <span v-if="showName" class="status-name ml-2">
            {{ userName }}
            <span class="status-text">({{ statusText }})</span>
          </span>
        </div>
      </template>

      <!-- Status-Menü-Inhalt -->
      <v-card min-width="300" class="status-menu-card">
        <v-card-title class="status-menu-title">
          Status setzen
        </v-card-title>

        <v-divider></v-divider>

        <v-list>
          <v-list-item
            v-for="status in statusOptions"
            :key="status.value"
            @click="setStatus(status.value)"
            :active="authStore.currentStatus === status.value"
          >
            <template v-slot:prepend>
              <v-icon :color="getStatusColor(status.value)">{{ status.icon }}</v-icon>
            </template>
            <v-list-item-title>{{ status.label }}</v-list-item-title>
          </v-list-item>
        </v-list>

        <v-divider></v-divider>

        <!-- Benutzerdefinierter Status -->
        <div class="custom-status-section pa-4">
          <v-text-field
            v-model="customStatusMessage"
            label="Benutzerdefinierte Nachricht"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="authStore.currentStatus !== 'custom'"
            placeholder="Was machst du gerade?"
            prepend-inner-icon="mdi-message-text-outline"
          ></v-text-field>

          <v-btn
            block
            color="primary"
            class="mt-3"
            :disabled="authStore.currentStatus !== 'custom' || !customStatusMessage"
            @click="setCustomStatus"
          >
            Nachricht speichern
          </v-btn>
        </div>
      </v-card>
    </v-menu>

    <!-- Separater Menü-Inhalt für externe Verwendung -->
    <v-card v-else-if="showDropdownContent" min-width="300" class="status-menu-card">
      <v-card-title class="status-menu-title">
        Status setzen
      </v-card-title>

      <v-divider></v-divider>

      <v-list>
        <v-list-item
          v-for="status in statusOptions"
          :key="status.value"
          @click="setStatus(status.value)"
          :active="authStore.currentStatus === status.value"
        >
          <template v-slot:prepend>
            <v-icon :color="getStatusColor(status.value)">{{ status.icon }}</v-icon>
          </template>
          <v-list-item-title>{{ status.label }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <v-divider></v-divider>

      <!-- Benutzerdefinierter Status -->
      <div class="custom-status-section pa-4">
        <v-text-field
          v-model="customStatusMessage"
          label="Benutzerdefinierte Nachricht"
          variant="outlined"
          density="compact"
          hide-details
          :disabled="authStore.currentStatus !== 'custom'"
          placeholder="Was machst du gerade?"
          prepend-inner-icon="mdi-message-text-outline"
        ></v-text-field>

        <v-btn
          block
          color="primary"
          class="mt-3"
          :disabled="authStore.currentStatus !== 'custom' || !customStatusMessage"
          @click="setCustomStatus"
        >
          Nachricht speichern
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script setup lang="ts">
 
import { ref, computed, onMounted, watch, toRefs } from 'vue';
import { useAuthStore, type UserStatus } from '@/stores/auth';

const props = defineProps({
  showName: {
    type: Boolean,
    default: false
  },
  showDropdownContent: {
    type: Boolean,
    default: false
  },
  hideAvatar: {
    type: Boolean,
    default: false
  }
});
 

const authStore = useAuthStore();
const customStatusMessage = ref('');
const { showName, showDropdownContent, hideAvatar } = toRefs(props);

  // Benutzer & Status
  const user = computed(() => authStore.user);
  const userName = computed(() => {
    if (!user.value) return 'Gast';
    return `${user.value.vorname || ''} ${user.value.nachname || ''}`.trim() || user.value.loginname;
  });

  // Status-Optionen für das Menü
  const statusOptions: Array<{label: string, value: UserStatus, icon: string}> = [
    { label: 'Online', value: 'online', icon: 'mdi-circle' },
    { label: 'Abwesend', value: 'away', icon: 'mdi-clock-outline' },
    { label: 'Beschäftigt', value: 'busy', icon: 'mdi-minus-circle' },
    { label: 'Mobile', value: 'mobile', icon: 'mdi-cellphone' },
    { label: 'Benutzerdefiniert', value: 'custom', icon: 'mdi-pencil' },
    { label: 'Offline', value: 'offline', icon: 'mdi-circle-outline' },
  ];

  // Status-Farbe basierend auf dem aktuellen Status
  const statusColor = computed(() => getStatusColor(authStore.currentStatus));

  // Status-Text für die Anzeige
  const statusText = computed(() => {
    const status = authStore.currentStatus;

    if (status === 'custom' && authStore.currentStatusMessage) {
      return authStore.currentStatusMessage;
    }

    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.label : 'Offline';
  });

  // Funktionen
  const getInitials = (firstName?: string, lastName?: string): string => {
    if (!firstName && !lastName) return '?';
    return `${firstName ? firstName.charAt(0).toUpperCase() : ''}${
      lastName ? lastName.charAt(0).toUpperCase() : ''}`;
  };

  const getStatusColor = (status: UserStatus): string => {
    switch (status) {
      case 'online': return 'success';
      case 'away': return 'amber';
      case 'busy': return 'error';
      case 'custom': return 'info';
      case 'mobile': return 'purple';
      case 'offline': default: return 'grey';
    }
  };

  const setStatus = async (status: UserStatus) => {
  if (status === 'custom') {
    // Bei Auswahl von "Benutzerdefiniert" den Status ändern und Nachricht vorbereiten
    customStatusMessage.value = authStore.currentStatusMessage || '';
    await authStore.updateUserStatus('custom', customStatusMessage.value);
  } else {
    await authStore.updateUserStatus(status);
  }
};

  const setCustomStatus = async () => {
    if (customStatusMessage.value.trim()) {
      await authStore.updateUserStatus('custom', customStatusMessage.value);
    }
  };

  // Initialisierung
  onMounted(() => {
    if (authStore.user?.custom_status_message) {
      customStatusMessage.value = authStore.user.custom_status_message;
    }
  });

  // Aktualisiere das Textfeld, wenn sich der benutzerdefinierte Status ändert
  watch(() => authStore.user?.custom_status_message, (newMessage) => {
    if (newMessage) {
      customStatusMessage.value = newMessage;
    }
  });
  </script>

  <style scoped>
  .user-status-component {
    display: inline-block;
  }

  .status-indicator {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px;
    border-radius: 50px;
    transition: background-color 0.2s ease;
  }

  .status-indicator:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .status-name {
    font-size: 14px;
    white-space: nowrap;
    color: rgba(255, 255, 255, 0.9);
  }

  .status-text {
    font-size: 0.8em;
    opacity: 0.75;
    margin-left: 4px;
  }

  .status-menu-card {
    background: linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(20, 20, 28, 0.98)) !important;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    overflow: hidden;
  }

  .status-menu-title {
    padding: 16px;
    font-size: 1.1rem;
    background: rgba(33, 150, 243, 0.1);
    border-bottom: 1px solid rgba(33, 150, 243, 0.2);
  }

  .custom-status-section {
    background: rgba(0, 0, 0, 0.2);
  }
  </style>
